// src/components/prescriptions/PreviewPaper.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

// --- Constantes A4 (mm) ---
const MM = (mm) => `${mm}mm`;
const PAGE_W_MM = 210;
const PAGE_H_MM = 297;
const PAD_MM = 15;
const CONTENT_W_MM = PAGE_W_MM - PAD_MM * 2;
const FOOTER_GAP_MM = 3;

// ---------------- Header ----------------
function Header({ branding, title }) {
  const {
    show_logo = true,
    logo_url,
    logo_size_px = 140,
    clinic_name = "",
    doctor_name = "",
    specialty = "",
    cro_number = "",
    show_doctor_name = true,
    show_cro = true,
    header_align = "center",
    primary_color = "#0ea5a4",
    font_family = "Arial",
  } = branding || {};

  const align =
    header_align === "left"
      ? "items-start text-left"
      : header_align === "right"
      ? "items-end text-right"
      : "items-center text-center";

  return (
    <div className={`flex flex-col ${align}`} style={{ fontFamily: font_family }}>
      {show_logo && logo_url ? (
        <div
          className="mb-2 flex items-center justify-center"
          style={{ height: `${logo_size_px}px` }}
        >
          <img
            src={logo_url}
            alt="logo"
            style={{ maxHeight: "100%", width: "auto", maxWidth: "100%" }}
            className="object-contain"
          />
        </div>
      ) : null}

      {clinic_name ? (
        <div className="text-[12pt] font-semibold" style={{ color: primary_color }}>
          {clinic_name}
        </div>
      ) : null}
      {show_doctor_name && doctor_name ? (
        <div className="text-[12pt]">{doctor_name}</div>
      ) : null}
      {specialty ? <div className="text-[10pt] text-gray-700">{specialty}</div> : null}
      {show_cro && cro_number ? (
        <div className="text-[10pt] text-gray-700">
          CRO {branding?.cro_state ? `${branding.cro_state} ` : ''}
          {cro_number}
        </div>
      ) : null}
      {title ? <div className="mt-2 text-[13pt] font-semibold">{title}</div> : null}
    </div>
  );
}

// --------------- Watermark ---------------
function Watermark({ branding }) {
  const {
    show_watermark = true,
    watermark_type = "svg", // "svg" (padrão do sistema) ou "image" (upload do usuário)
    watermark_image_url = "",
    watermark_opacity = 0.08,
    watermark_scale = 1.2,
    watermark_position = "center",
  } = branding || {};

  const [imgError, setImgError] = useState(false);
  if (!show_watermark) return null;

  const base = { position: "absolute", pointerEvents: "none", opacity: watermark_opacity };
  const pos =
    watermark_position === "bottom-right"
      ? { right: "20mm", bottom: "20mm", transform: `scale(${watermark_scale})` }
      : { left: "50%", top: "50%", transform: `translate(-50%,-50%) scale(${watermark_scale})` };

  // Upload do usuário
  if (watermark_type === "image" && watermark_image_url && !imgError) {
    return (
      <img
        src={watermark_image_url}
        alt=""
        style={{ ...base, ...pos, maxWidth: "60%", maxHeight: "60%" }}
        onError={() => setImgError(true)}
      />
    );
  }

  // Padrão do sistema: /dente.svg em public
  const publicUrl = process.env.PUBLIC_URL || "";
  const defaultPath = `${publicUrl}/dente.svg`;

  if (!imgError) {
    return (
      <img
        src={defaultPath}
        alt=""
        style={{ ...base, ...pos, maxWidth: "60%", maxHeight: "60%" }}
        onError={() => setImgError(true)}
      />
    );
  }

  // Fallback inline (se /dente.svg não carregar)
  return (
    <svg
      viewBox="0 0 200 200"
      style={{ ...base, ...pos, width: "60mm", height: "60mm" }}
      fill="none"
      stroke="#0ea5a4"
      strokeWidth="3"
    >
      <path d="M100 10c-25 0-60 15-60 60 0 25 15 40 25 55 10 15 15 40 35 40s25-25 35-40c10-15 25-30 25-55 0-45-35-60-60-60z" />
      <path d="M85 60c5-5 15-5 20 0" />
    </svg>
  );
}

// --------------- Carimbo & Assinaturas (apenas última página) ---------------
function Stamp({ branding }) {
  const { doctor_name = "", specialty = "", cro_number = "" } = branding || {};

  return (
    <div
      className="inline-flex flex-col items-center px-3 py-1.5 border-2 rounded-md"
      style={{ lineHeight: 1.1, gap: "2px", letterSpacing: "0.3px" }}
    >
      {/* Nome — levemente maior */}
      <div className="text-[10.5pt] font-semibold">
        {doctor_name || "[NOME DO DENTISTA]"}
      </div>

      {/* Especialidade — linha própria */}
      <div className="text-[9pt] text-gray-700">
        {specialty || "[ESPECIALIDADE]"}
      </div>

      {/* CRO — linha própria */}
      <div className="text-[9pt] text-gray-700">
        {cro_number
          ? `CRO ${branding?.cro_state ? `${branding.cro_state} ` : ''}${cro_number}`
          : "CRO 00000"}
      </div>
    </div>
  );
}

function SignaturesBlock({ branding, content }) {
  const {
    show_signature = false,
    signature_url = "",
    show_stamp = true,
    // NOVO: tamanho configurável da assinatura
    signature_size_px = 72,
  } = branding || {};

  const hasPatientSig = Boolean(content?.patient_signature_url);
  const showDentistSig = Boolean(show_signature && signature_url);
  const showDentistStamp = Boolean(show_stamp);

  if (!hasPatientSig && !showDentistSig && !showDentistStamp) return null;

  // NOVO: margem de ~1,5 cm entre o texto e as assinaturas/carimbo
  return (
    <div style={{ marginTop: "15mm" }}>
      {hasPatientSig && (
        <div className="flex flex-col items-center justify-center mb-4">
          <img
            src={content.patient_signature_url}
            alt="assinatura paciente"
            style={{ maxHeight: 68, objectFit: "contain" }}
          />
          <div className="text-center text-[10pt] mt-1">
            <div className="border-t w-48 mx-auto mb-1" />
            Assinatura do Paciente
          </div>
          {(content.signed_by_name ||
            content.signed_by_cpf ||
            content.signed_at ||
            content.signature_validation_code ||
            content.signed_ip) && (
            <div className="text-center text-[8.5pt] text-gray-600 mt-1">
              {content.signed_by_name ? (
                <div>
                  {content.signed_by_name}
                  {content.signed_by_cpf ? ` • CPF ${content.signed_by_cpf}` : ""}
                </div>
              ) : null}
              {content.signed_at ? (
                <div>Assinado em {new Date(content.signed_at).toLocaleString()}</div>
              ) : null}
              {content.signed_ip ? <div>IP: {content.signed_ip}</div> : null}
              {content.signature_validation_code ? (
                <div>Código: {content.signature_validation_code}</div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {(showDentistSig || showDentistStamp) && (
        <div className="flex flex-col items-center justify-center">
          {showDentistSig && (
            <img
              src={signature_url}
              alt="assinatura dentista"
              className="-mb-1"
              // NOVO: usa altura configurável
              style={{ maxHeight: signature_size_px, objectFit: "contain" }}
            />
          )}
          {showDentistStamp && <Stamp branding={branding} />}
        </div>
      )}
    </div>
  );
}

// ---------------- Footer (sem assinaturas) ----------------
function Footer({ branding, pageIndex, pageCount }) {
  const {
    email = "",
    phone = "",
    address = "",
    show_footer_phone = true,
    show_footer_email = true,
    show_footer_address = true,
    show_footer_whatsapp = false,
    font_family = "Arial",
  } = branding || {};

  return (
    <div style={{ fontFamily: font_family }}>
      {(show_footer_phone ||
        show_footer_whatsapp ||
        show_footer_email ||
        show_footer_address) && (
        <div className="mt-2 pt-2 border-t text-[9pt] text-gray-700 flex flex-col items-start gap-1">

          <div className="flex flex-wrap gap-x-4">
            {show_footer_phone && phone ? <span>☎ {phone}</span> : null}
            {show_footer_email && email ? <span>✉ {email}</span> : null}
          </div>

          <div className="flex flex-wrap gap-x-4">
            {show_footer_address && address ? <span>📍 {address}</span> : null}
          </div>

        </div>
      )}
      <div className="mt-1 text-left text-[9pt] text-gray-500" style={{ minHeight: 14 }}>
        Página {pageIndex + 1} de {pageCount}
      </div>
    </div>
  );
}

// ---------------- Utilidades ----------------
function mmToPx(mm) {
  return Math.round(mm * 3.78);
}
function splitIntoBlocks(fullText) {
  if (!fullText) return [];
  const NB_OPEN = "[[NB]]";
  const NB_CLOSE = "[[/NB]]";
  const blocks = [];
  let i = 0;
  while (i < fullText.length) {
    const open = fullText.indexOf(NB_OPEN, i);
    if (open === -1) {
      const tail = fullText.slice(i).trim();
      if (tail) blocks.push({ nb: false, text: tail });
      break;
    }
    const before = fullText.slice(i, open).trim();
    if (before) blocks.push({ nb: false, text: before });
    const close = fullText.indexOf(NB_CLOSE, open + NB_OPEN.length);
    const inside =
      close !== -1
        ? fullText.slice(open + NB_OPEN.length, close).trim()
        : fullText.slice(open + NB_OPEN.length).trim();
    if (inside) blocks.push({ nb: true, text: inside });
    i = close === -1 ? fullText.length : close + NB_CLOSE.length;
  }
  return blocks;
}
function textToParagraphs(t) {
  return t
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// ---------------- Componente principal ----------------
export default function PreviewPaper({
  captureRef,
  branding = {},
  content = {},
  variant = "document", // "contract" ou "document"
}) {
  const outerRef = captureRef || useRef(null);
  const isImageLayout = branding?.layout_mode === "image" && branding?.layout_image_url;

  // medidores invisíveis
  const headerMeasureRef = useRef(null);
  const footerMeasureRef = useRef(null);
  const measureRef = useRef(null);
  const signatureMeasureRef = useRef(null);

  // monta texto (com DAS PARTES em contratos)
  const composedText = useMemo(() => {
    const body = (content?.guidance_text || content?.contract_text || "").trim();
    let pre = "";

    if (variant === "contract" && content?.parties_block) {
      const hasDasPartes = /^DAS PARTES\b/i.test(body);
      if (!hasDasPartes) pre = `[[NB]]${content.parties_block}[[/NB]]\n\n`;
    } else if (!/^ATESTADO ODONTOLÓGICO|^PACIENTE:/i.test(body)) {
      const infos = [];
      if (content?.patient_name) infos.push(`Paciente: ${content.patient_name}`);
      if (content?.patient_cpf) infos.push(`CPF: ${content.patient_cpf}`);
      if (content?.patient_address) infos.push(`Endereço: ${content.patient_address}`);
      if (infos.length) pre = `[[NB]]${infos.join("\n")}[[/NB]]\n\n${pre}`;
    }

    let post = "";
    const bodyHasDateByExtenso = /\d{2}\s+de\s+[a-zçãé]+\s+de\s+\d{4}/i.test(body);

    if (content?.issue_date && !bodyHasDateByExtenso) {
      const dt = new Date(content.issue_date);
      const dataStr = isNaN(+dt) ? String(content.issue_date) : dt.toLocaleDateString();
      post = `\n\n[[NB]]Data: ${dataStr}[[/NB]]`;
    }

    return `${pre}${body}${post}`.trim();
  }, [variant, content]);

  const [pages, setPages] = useState([[]]);
  const [needsExtraSignaturePage, setNeedsExtraSignaturePage] = useState(false);

  // Paginador (conteúdo)
  useEffect(() => {
    const pageH = mmToPx(PAGE_H_MM);
    const pad = mmToPx(PAD_MM);
    const gap = mmToPx(FOOTER_GAP_MM);
    const headerReserved = isImageLayout
      ? mmToPx(branding?.layout_image_header_mm || 0)
      : headerMeasureRef.current?.offsetHeight || 0;
    const footerReserved = isImageLayout
      ? mmToPx(branding?.layout_image_footer_mm || 0)
      : footerMeasureRef.current?.offsetHeight || 0;
    const available = pageH - headerReserved - footerReserved - pad * 2 - gap;

    const meas = measureRef.current;
    if (!meas) return;
    meas.innerHTML = "";

    const blocks = splitIntoBlocks(composedText);
    const outPages = [];
    let current = [];
    let currentHeight = 0;

    const addParagraphEl = (txt) => {
      const p = document.createElement("div");
      p.className = "text-[12pt] leading-relaxed whitespace-pre-wrap break-words mb-2";
      p.textContent = txt;
      meas.appendChild(p);
      return p;
    };
    const getHeight = () => meas.scrollHeight;
    const newPage = () => {
      outPages.push(current);
      current = [];
      currentHeight = 0;
      meas.innerHTML = "";
    };

    for (const block of blocks) {
      const paras = textToParagraphs(block.text);
      const startH = getHeight();
      const addedEls = [];
      for (const txt of paras) addedEls.push(addParagraphEl(txt));
      const blockH = getHeight() - startH;

      const fits = currentHeight + blockH <= available;

      if (fits) {
        current.push(...paras);
        currentHeight += blockH;
      } else {
        if (block.nb && current.length > 0) {
          newPage();
          for (const el of addedEls) el.remove();
          const reEls = [];
          const baseH = getHeight();
          for (const txt of paras) reEls.push(addParagraphEl(txt));
          const blockH2 = getHeight() - baseH;

          if (blockH2 > available) {
            meas.innerHTML = "";
            for (const txt of paras) {
              const h0 = getHeight();
              const el = addParagraphEl(txt);
              const h1 = getHeight();
              const paraH = h1 - h0;
              if (paraH > available) {
                el.remove();
                const lines = txt.split(/\n/).map((s) => s || " ");
                for (const line of lines) {
                  const h2 = getHeight();
                  const elLine = addParagraphEl(line);
                  const h3 = getHeight();
                  const lineH = h3 - h2;
                  if (currentHeight + lineH > available) newPage();
                  current.push(line);
                  currentHeight += lineH;
                }
              } else {
                if (currentHeight + paraH > available) newPage();
                current.push(txt);
                currentHeight += paraH;
              }
            }
          } else {
            current.push(...paras);
            currentHeight += blockH2;
          }
        } else {
          for (const el of addedEls) el.remove();

          for (const txt of paras) {
            const h0 = getHeight();
            const el = addParagraphEl(txt);
            const h1 = getHeight();
            const paraH = h1 - h0;

            if (paraH > available) {
              el.remove();
              const lines = txt.split(/\n/).map((s) => s || " ");
              for (const line of lines) {
                const h2 = getHeight();
                const elLine = addParagraphEl(line);
                const h3 = getHeight();
                const lineH = h3 - h2;

                if (currentHeight + lineH > available) newPage();
                current.push(line);
                currentHeight += lineH;
              }
            } else {
              if (currentHeight + paraH > available) newPage();
              current.push(txt);
              currentHeight += paraH;
            }
          }
        }
      }

      // ressincroniza medidor com conteúdo atual
      meas.innerHTML = "";
      for (const txt of current) addParagraphEl(txt);
      currentHeight = getHeight();
    }
    outPages.push(current);
    setPages(outPages);
  }, [composedText, branding, isImageLayout]);

  // Verifica se cabe assinaturas na última página; se não, cria página extra
  useEffect(() => {
    const hasAnySignature =
      Boolean(content?.patient_signature_url) ||
      Boolean((branding?.show_signature && branding?.signature_url) || branding?.show_stamp);

    if (!hasAnySignature) {
      setNeedsExtraSignaturePage(false);
      return;
    }

    const pageH = mmToPx(PAGE_H_MM);
    const pad = mmToPx(PAD_MM);
    const gap = mmToPx(FOOTER_GAP_MM);
    const headerReserved = isImageLayout
      ? mmToPx(branding?.layout_image_header_mm || 0)
      : headerMeasureRef.current?.offsetHeight || 0;
    const footerReserved = isImageLayout
      ? mmToPx(branding?.layout_image_footer_mm || 0)
      : footerMeasureRef.current?.offsetHeight || 0;
    const available = pageH - headerReserved - footerReserved - pad * 2 - gap;

    const meas = measureRef.current;
    if (!meas) return;
    meas.innerHTML = "";
    const last = pages[pages.length - 1] || [];
    for (const txt of last) {
      const p = document.createElement("div");
      p.className = "text-[12pt] leading-relaxed whitespace-pre-wrap break-words mb-2";
      p.textContent = txt;
      meas.appendChild(p);
    }
    const lastContentH = meas.scrollHeight;

    // Altura estimada do bloco de assinaturas (usa DOM oculto)
    const sigCont = signatureMeasureRef.current;
    let sigH = 0;
    if (sigCont) sigH = sigCont.offsetHeight || 0;

    const free = available - lastContentH;
    setNeedsExtraSignaturePage(sigH > free);
  }, [pages, branding, content, isImageLayout]);

  const effectivePageCount = pages.length + (needsExtraSignaturePage ? 1 : 0);

  return (
    <div ref={outerRef}>
      {/* Medidores invisíveis */}
      <div
        style={{
          position: "absolute",
          visibility: "hidden",
          left: "-10000px",
          top: 0,
          width: MM(PAGE_W_MM),
        }}
      >
        <div
          ref={headerMeasureRef}
          style={{ width: MM(CONTENT_W_MM), margin: "0 auto", paddingTop: MM(0.1) }}
        >
          <Header branding={branding} title={content?.title} />
        </div>
        <div
          ref={footerMeasureRef}
          style={{ width: MM(CONTENT_W_MM), margin: "0 auto", paddingBottom: MM(0.1) }}
        >
          <Footer branding={branding} pageIndex={0} pageCount={effectivePageCount} />
        </div>

        {/* Bloco de assinaturas para medição */}
        <div
          ref={signatureMeasureRef}
          style={{
            width: MM(CONTENT_W_MM),
            margin: "0 auto",
            paddingTop: MM(0.1),
            paddingBottom: MM(0.1),
          }}
        >
          <SignaturesBlock branding={branding} content={content} />
        </div>

        <div
          ref={measureRef}
          style={{
            width: MM(CONTENT_W_MM),
            margin: "0 auto",
            fontSize: "12pt",
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        />
      </div>

      {/* Páginas A4 */}
      {pages.map((paras, idx) => (
        <div
          key={idx}
          className="a4-page relative bg-white border shadow mx-auto mb-4"
          style={{
            width: MM(PAGE_W_MM),
            height: MM(PAGE_H_MM),
            padding: MM(PAD_MM),
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            backgroundImage: isImageLayout ? `url(${branding.layout_image_url})` : undefined,
            backgroundSize: isImageLayout ? "cover" : undefined,
            backgroundPosition: isImageLayout ? "center center" : undefined,
            backgroundRepeat: isImageLayout ? "no-repeat" : undefined,
          }}
        >
          {!isImageLayout && <Watermark branding={branding} />}

          <div style={{ width: "100%" }}>
            <div style={{ width: MM(CONTENT_W_MM), margin: "0 auto" }}>
              {!isImageLayout && <Header branding={branding} title={content?.title} />}
            </div>
          </div>

          <div
            style={{
              width: MM(CONTENT_W_MM),
              margin: `${FOOTER_GAP_MM}mm auto 0`,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              paddingTop: isImageLayout ? MM(branding?.layout_image_header_mm || 0) : undefined,
              paddingBottom: isImageLayout
                ? MM(branding?.layout_image_footer_mm || 0)
                : undefined,
            }}
          >
            {paras.map((txt, j) => (
              <p
                key={j}
                className="text-[12pt] leading-relaxed whitespace-pre-wrap break-words mb-2"
              >
                {txt}
              </p>
            ))}

            {/* Assinaturas apenas na última página e apenas se couber */}
            {idx === pages.length - 1 && !needsExtraSignaturePage && (
              <SignaturesBlock branding={branding} content={content} />
            )}
          </div>

          <div style={{ width: MM(CONTENT_W_MM), margin: "0 auto" }}>
            {!isImageLayout && (
              <Footer branding={branding} pageIndex={idx} pageCount={effectivePageCount} />
            )}
          </div>
        </div>
      ))}

      {/* Página adicional só de assinaturas, se necessário */}
      {needsExtraSignaturePage && (
        <div
          className="a4-page relative bg-white border shadow mx-auto mb-4"
          style={{
            width: MM(PAGE_W_MM),
            height: MM(PAGE_H_MM),
            padding: MM(PAD_MM),
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            backgroundImage: isImageLayout ? `url(${branding.layout_image_url})` : undefined,
            backgroundSize: isImageLayout ? "cover" : undefined,
            backgroundPosition: isImageLayout ? "center center" : undefined,
            backgroundRepeat: isImageLayout ? "no-repeat" : undefined,
          }}
        >
          {!isImageLayout && <Watermark branding={branding} />}

          <div style={{ width: "100%" }}>
            <div style={{ width: MM(CONTENT_W_MM), margin: "0 auto" }}>
              {!isImageLayout && <Header branding={branding} title={content?.title} />}
            </div>
          </div>

          <div
            style={{
              width: MM(CONTENT_W_MM),
              margin: `${FOOTER_GAP_MM}mm auto 0`,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              alignItems: "flex-start",
              paddingTop: isImageLayout ? MM(branding?.layout_image_header_mm || 0) : undefined,
              paddingBottom: isImageLayout
                ? MM(branding?.layout_image_footer_mm || 0)
                : undefined,
            }}
          >
            <div style={{ width: "100%" }}>
              <SignaturesBlock branding={branding} content={content} />
            </div>
          </div>

          <div style={{ width: MM(CONTENT_W_MM), margin: "0 auto" }}>
            {!isImageLayout && (
              <Footer
                branding={branding}
                pageIndex={effectivePageCount - 1}
                pageCount={effectivePageCount}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
